import React, { useEffect, useState } from "react";
import { Heart, Save, Trophy, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { formatINR } from "../../lib/utils.fmt";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function AdminLoyalty() {
  const [settings, setSettings] = useState(null);
  const [board, setBoard] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    points_per_rupee: 0.1,
    redeem_rate_paise: 50,
    min_redeem_points: 100,
    is_enabled: true,
  });

  const load = async () => {
    const [s, lb] = await Promise.all([
      api.get("/api/admin/loyalty/settings").then((r) => r.data),
      api.get("/api/admin/loyalty/leaderboard").then((r) => r.data).catch(() => ({ rows: [] })),
    ]);
    setSettings(s);
    setForm({
      points_per_rupee: s.points_per_rupee,
      redeem_rate_paise: s.redeem_rate_paise,
      min_redeem_points: s.min_redeem_points,
      is_enabled: s.is_enabled,
    });
    setBoard(lb.rows || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/api/admin/loyalty/settings", {
        points_per_rupee: parseFloat(form.points_per_rupee),
        redeem_rate_paise: parseInt(form.redeem_rate_paise),
        min_redeem_points: parseInt(form.min_redeem_points),
        is_enabled: !!form.is_enabled,
      });
      toast.success("Loyalty settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <LoadingState fullHeight testId="admin-loyalty-loading" />;

  const exampleEarn = Math.floor(100 * (parseFloat(form.points_per_rupee) || 0));
  const exampleRedeem = ((parseInt(form.min_redeem_points) || 0) * (parseInt(form.redeem_rate_paise) || 0)) / 100;

  return (
    <div className="space-y-6 page-enter" data-testid="admin-loyalty-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Module</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Heart className="w-7 h-7 text-brand-clay"/> Loyalty Program
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Reward repeat customers with points they can redeem to their wallet.
        </p>
      </div>

      {/* Settings */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6 space-y-5" data-testid="loyalty-settings-card">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-extrabold tracking-tight">Settings</h2>
          <label className="inline-flex items-center gap-2 text-sm font-bold cursor-pointer" data-testid="loyalty-toggle-label">
            <input type="checkbox" checked={!!form.is_enabled}
                   onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                   className="w-5 h-5 rounded" data-testid="loyalty-enabled-toggle"/>
            {form.is_enabled ? "Enabled" : "Disabled"}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Points per ₹" hint="0.1 = 1 pt per ₹10 spent">
            <Input type="number" step="0.01" value={form.points_per_rupee}
                   onChange={(e) => setForm({ ...form, points_per_rupee: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="loyalty-points-per-rupee"/>
          </Field>
          <Field label="Redemption rate (paise/pt)" hint="50 = ₹0.50 per point on redemption">
            <Input type="number" value={form.redeem_rate_paise}
                   onChange={(e) => setForm({ ...form, redeem_rate_paise: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="loyalty-redeem-rate"/>
          </Field>
          <Field label="Minimum redeem (pts)" hint="Customer needs at least this many points to redeem">
            <Input type="number" value={form.min_redeem_points}
                   onChange={(e) => setForm({ ...form, min_redeem_points: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="loyalty-min-redeem"/>
          </Field>
        </div>

        <div className="rounded-2xl bg-brand-cream p-4 text-sm" data-testid="loyalty-preview">
          <div className="font-bold text-neutral-700 mb-1">Preview</div>
          <div>A customer spending ₹100 will earn <b>{exampleEarn} points</b>.</div>
          <div>Redeeming {form.min_redeem_points} points credits <b>{formatINR(exampleRedeem)}</b> to their wallet.</div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy}
                  className="h-12 px-6 rounded-2xl bg-brand-ink text-white font-extrabold"
                  data-testid="save-loyalty">
            <Save className="w-4 h-4 mr-1.5"/> {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </section>

      {/* Leaderboard */}
      <section data-testid="loyalty-leaderboard-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2">
            <Trophy className="w-5 h-5"/> Top customers by points
          </h2>
          <Button onClick={load} variant="ghost" className="h-9 rounded-2xl text-xs" data-testid="refresh-leaderboard">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        </div>
        <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
          {board.length === 0 ? (
            <EmptyState icon={Trophy} size="sm"
                        title="No points awarded yet"
                        description={<>Once a delivery is marked <b>delivered</b>, points will start accruing.</>}
                        testId="leaderboard-empty" />
          ) : (
            <table className="w-full">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">#</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-right px-5 py-3">Points</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {board.map((r, i) => (
                  <tr key={r.id} data-testid={`leaderboard-row-${i}`}>
                    <td className="px-5 py-3 font-mono font-bold">#{i + 1}</td>
                    <td className="px-5 py-3">{r.name || "—"}</td>
                    <td className="px-5 py-3 font-mono text-xs text-neutral-500">{r.phone}</td>
                    <td className="px-5 py-3 text-right font-mono font-extrabold text-brand-clay">{r.loyalty_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-neutral-400 mt-1">{hint}</div>}
    </div>
  );
}
